import { cn } from "@/lib/utils";

/**
 * An `img` that fades in once loaded, so remote artwork never pops in over
 * its placeholder. Static assets (logos, store badges) don't go through it —
 * they are local and tiny, a fade would only delay them.
 *
 * Stateless on purpose: the flag is a `data-loaded` attribute set from the
 * DOM, so a cover costs no extra render, an image already in the cache is
 * flagged before first paint on Chromium and WebKit (no fade replayed on a
 * remount), and a priority cover's LCP doesn't wait on React. Firefox reports
 * `complete` false for a cached `<picture>` at that point, so it waits for
 * `load` and replays the 300 ms fade. A broken image (`naturalWidth` 0) never
 * gets the flag and stays invisible.
 */
const FadeInImage: React.FC<Omit<React.ComponentProps<"img">, "ref">> = ({
  className,
  onLoad,
  ...props
}) => (
  <img
    {...props}
    // A new `src` is a new node: `data-loaded` would otherwise outlive the
    // image it was set for, skipping the fade and showing a broken icon if
    // the new one fails.
    key={props.src}
    ref={(img) => {
      if (img?.complete && img.naturalWidth > 0) img.dataset.loaded = "";
    }}
    className={cn(
      "opacity-0 transition-opacity duration-300 data-loaded:opacity-100 motion-reduce:transition-none",
      className,
    )}
    onLoad={(event) => {
      event.currentTarget.dataset.loaded = "";
      onLoad?.(event);
    }}
  />
);

export default FadeInImage;
